import { useCallback, useEffect } from 'react';

import { InteractableMap } from './use-interactable-map';
import { ContextMenu } from './use-mindmap-context-menu';
import { CopyCutPaste } from './use-mindmap-copy-cut-paste';
import { History } from './use-mindmap-history';
import { KeyboardNavigation } from './use-mindmap-keyboard-navigation';
import { Manipulation } from './use-mindmap-manipulation';
import { Selection } from './use-mindmap-selection';

export interface Keyboard {}

type UseKeyboard = {
  selection: Selection;
  copyCutPaste: CopyCutPaste;
  manipulation: Manipulation;
  keyboardNavigation: KeyboardNavigation;
  history: History;
  interactableMap: InteractableMap;
  contextMenu: ContextMenu;
};

export const useMindmapKeyboard = ({
  selection: { selectedNodeIds, unselectNodes, isEditingNode, setIsEditingNode, setSelectedNodeIds, selectAllNodes },
  copyCutPaste: {
    copySelectedNodes,
    pasteNodes,
    cutSelectedNodes,
    revertCutting,
    copiedTrees,
    hasCutNodes,
    setHasCutNodes,
  },
  manipulation: { removeNode, addChild, addSibling },
  keyboardNavigation: { selectHigherNode, selectLowerNode, selectSiblingNode },
  history: {
    actions: { redo, undo },
    nodeMapChangeHistory,
    currentUndoRedoStep,
  },
  interactableMap: { resetScaling },
  contextMenu: { activeMenu },
}: UseKeyboard): Keyboard => {
  const checkKeyPress = useCallback(
    (e: KeyboardEvent) => {
      let { ctrlKey, metaKey, key } = e;
      key = key.toLowerCase();

      if (activeMenu) {
        // dont register any shortcuts when context menu is opened
        return;
      }

      // Map interaction shortcuts should not be callable while editing a node
      if (!isEditingNode) {
        // Check if CTRL or CMD was pressed
        if (ctrlKey || metaKey) {
          if (key == 'c') {
            // copy
            copySelectedNodes();
          } else if (key == 'v') {
            // paste
            pasteNodes(copiedTrees, selectedNodeIds, { keepIds: hasCutNodes });
            setHasCutNodes(false);
          } else if (key == 'x') {
            // cut
            cutSelectedNodes();
            setHasCutNodes(true);
          } else if (key == 'z') {
            undo();
          } else if (key == 'y') {
            redo();
          } else if (key == 'a') {
            // select all nodes
            selectAllNodes();
          } else if (key == '0') {
            resetScaling();
          }
        }
      }
      if (key == 'escape') {
        if (isEditingNode) {
          // get out of editing mode
          setIsEditingNode(false);
        } else {
          // revert cutting
          revertCutting();
        }
      }
      if (!isEditingNode) {
        if (key == 'delete') {
          // delete
          removeNode(...selectedNodeIds);
          unselectNodes();
        } else if (key == 'enter') {
          // create sibling(s) and select them
          const children = addSibling(false, ...selectedNodeIds);
          if (children.length > 0) setSelectedNodeIds(children.map(({ id }) => id));
        } else if (key === 'tab') {
          // create child node(s) and select them
          const children = addChild(...selectedNodeIds);
          setSelectedNodeIds(children.map(({ id }) => id));
          // prevent instant unselecting of node
          e.preventDefault();
        } else if (key === 'arrowup') {
          // move up with arrow key
          selectHigherNode({ append: ctrlKey });
        } else if (key === 'arrowdown') {
          // move down with arrow key
          selectLowerNode({ append: ctrlKey });
        } else if (key === 'arrowleft') {
          // move left with arrow key
          selectSiblingNode({ append: ctrlKey, left: true });
        } else if (key === 'arrowright') {
          // move right with arrow key
          selectSiblingNode({ append: ctrlKey, left: false });
        }
      }
    },
    [selectedNodeIds, copiedTrees, nodeMapChangeHistory, currentUndoRedoStep, isEditingNode],
  );

  useEffect(() => {
    document.addEventListener('keydown', checkKeyPress);
    return () => {
      document.removeEventListener('keydown', checkKeyPress);
    };
  }, [checkKeyPress]);

  return {};
};
